import { NgTemplateOutlet } from '@angular/common';
import {
  inject,
  TemplateRef,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { SafeResourceUrl } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import type { BillingPlanTier } from '@core/billing-types';
import { filterDefined } from '@core/common-legacy';
import { HelpArticlesDirective } from '@core/help-articles-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { ThemeImagePipe } from '@core/theme-util';
import { HeadingComponent } from '@core/user-interface-legacy';

import { FeatureButtonComponent } from '../feature-button/feature-button.component';
import { FeatureGuardFacade } from '../feature-guard.facade';
import type { FeatureData } from '../feature-guard.model';
import { MediaSourceType } from '../feature-guard.model';

@Component({
  imports: [
    FeatureButtonComponent,
    TranslatePipe,
    HeadingComponent,
    HelpArticlesDirective,
    NgTemplateOutlet,
    ThemeImagePipe,
  ],
  selector: 'rt-feature-guard-content',
  templateUrl: './feature-guard-content.component.html',
  styleUrls: ['./feature-guard-content.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.no-content-width]': 'featureData.noContentWidth',
    '[attr.plan]': 'availableFromPlan()',
  },
})
export class FeatureGuardContentComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  public readonly facade = inject(FeatureGuardFacade);

  public readonly availableFromPlan = input<BillingPlanTier | null>(undefined!);
  public readonly availableInNewPlans = input<boolean>(false);

  @Input() public featureData: FeatureData;
  @Input() public canUpgrade: boolean;
  @Input() public customUpgradeButton: TemplateRef<any> | undefined;

  public openPlanPicker = output<void>();

  public helpArticleLang: keyof NonNullable<FeatureData['learnMoreUrlByLang']> = 'en';
  public MediaSourceType: typeof MediaSourceType = MediaSourceType;
  public safeUrl: SafeResourceUrl;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    if (this.featureData.mediaSourceType === MediaSourceType.YoutubeVideo) {
      // eslint-disable-next-line ban/ban
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        <string>this.featureData.mediaSource,
      );
    }
    if (this.featureData.helpUrl || this.featureData.helpArticleKey) return;
    // TODO after extracting currentCompany
    this.store
      .select(AccountSelectors.currentCompanyWithUser)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()), filterDefined)
      .subscribe(({ currentCompany, currentUser }) => {
        const preferredLanguage = <keyof NonNullable<FeatureData['learnMoreUrlByLang']> | 'auto'>(
          currentUser!.preferredLanguage
        );
        const currentCompanyLanguage = <keyof NonNullable<FeatureData['learnMoreUrlByLang']>>(
          currentCompany!.language
        );
        this.helpArticleLang =
          preferredLanguage === 'auto' ? currentCompanyLanguage || 'en' : preferredLanguage;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
